import React, { useRef, useEffect, useState, useMemo } from 'react';

interface TwinklingGoldBorderProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Component that creates an animated twinkling gold border
 * The actual dashes of the dotted border twinkle with rotating gradients and sparkle effects
 */
export function TwinklingGoldBorder({ children, className = '' }: TwinklingGoldBorderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Calculate dash spacing based on actual container dimensions
  const dashLength = 6; // Length of each dash in pixels
  const dashGap = 4; // Gap between dashes in pixels
  const dashSegment = dashLength + dashGap;
  
  const borderOffset = 1; // Offset from edge for border positioning
  const effectiveWidth = dimensions.width > 0 ? dimensions.width : 200;
  const effectiveHeight = dimensions.height > 0 ? dimensions.height : 100;
  
  // Calculate dashes per edge based on actual edge lengths
  const topDashes = Math.max(1, Math.floor(effectiveWidth / dashSegment));
  const rightDashes = Math.max(1, Math.floor(effectiveHeight / dashSegment));
  const bottomDashes = Math.max(1, Math.floor(effectiveWidth / dashSegment));
  const leftDashes = Math.max(1, Math.floor(effectiveHeight / dashSegment));
  
  const calculatedTotal = topDashes + rightDashes + bottomDashes + leftDashes;
  
  // Generate stable random delays and durations for each dash (only recalculate when total changes)
  const dashAnimations = useMemo(() => {
    return Array.from({ length: calculatedTotal }).map(() => ({
      delay: Math.random() * 2, // Random delay 0-2s for immediate random start
      duration: 1.5 + Math.random() * 1, // Random duration 1.5-2.5s
    }));
  }, [calculatedTotal]);
  
  return (
    <div ref={containerRef} className={`relative ${className}`} style={{ minHeight: '50px', minWidth: '100px' }}>
      <style>{`
        @keyframes borderGlow {
          0%, 100% {
            filter: drop-shadow(0 0 4px rgba(255, 215, 0, 0.4));
          }
          50% {
            filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.8)) drop-shadow(0 0 12px rgba(255, 215, 0, 0.4));
          }
        }
      `}</style>
      
      {/* SVG border with individual animated dashes */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ overflow: 'visible', zIndex: 0 }}
        viewBox={`0 0 ${effectiveWidth} ${effectiveHeight}`}
        preserveAspectRatio="none"
      >
        <defs>
          {/* Gold gradient for each dash - static, no circling */}
          {Array.from({ length: calculatedTotal }).map((_, i) => (
            <linearGradient key={`grad-${i}`} id={`dashGradient-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFD700" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#FFA500" stopOpacity="1" />
              <stop offset="100%" stopColor="#FFD700" stopOpacity="0.8" />
            </linearGradient>
          ))}
          
          {/* Sparkle filter */}
          <filter id="sparkle" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Create individual dash segments around the border */}
        {Array.from({ length: calculatedTotal }).map((_, i) => {
          const { delay, duration } = dashAnimations[i] || { delay: 0, duration: 2 };
          
          // Calculate position based on which edge and position on that edge
          let x1 = 0, y1 = 0, x2 = 0, y2 = 0;
          
          if (i < topDashes) {
            // Top edge (left to right)
            const edgeIndex = i;
            const spacing = (effectiveWidth - borderOffset * 2) / (topDashes + 1);
            x1 = borderOffset + spacing * (edgeIndex + 0.5);
            y1 = borderOffset;
            x2 = borderOffset + spacing * (edgeIndex + 0.5) + dashLength;
            y2 = borderOffset;
          } else if (i < topDashes + rightDashes) {
            // Right edge (top to bottom)
            const edgeIndex = i - topDashes;
            const spacing = (effectiveHeight - borderOffset * 2) / (rightDashes + 1);
            x1 = effectiveWidth - borderOffset;
            y1 = borderOffset + spacing * (edgeIndex + 0.5);
            x2 = effectiveWidth - borderOffset;
            y2 = borderOffset + spacing * (edgeIndex + 0.5) + dashLength;
          } else if (i < topDashes + rightDashes + bottomDashes) {
            // Bottom edge (right to left)
            const edgeIndex = i - topDashes - rightDashes;
            const spacing = (effectiveWidth - borderOffset * 2) / (bottomDashes + 1);
            x1 = effectiveWidth - borderOffset - spacing * (edgeIndex + 0.5);
            y1 = effectiveHeight - borderOffset;
            x2 = effectiveWidth - borderOffset - spacing * (edgeIndex + 0.5) - dashLength;
            y2 = effectiveHeight - borderOffset;
          } else {
            // Left edge (bottom to top)
            const edgeIndex = i - topDashes - rightDashes - bottomDashes;
            const spacing = (effectiveHeight - borderOffset * 2) / (leftDashes + 1);
            x1 = borderOffset;
            y1 = effectiveHeight - borderOffset - spacing * (edgeIndex + 0.5);
            x2 = borderOffset;
            y2 = effectiveHeight - borderOffset - spacing * (edgeIndex + 0.5) - dashLength;
          }
          
          return (
            <g key={i}>
              {/* Main dash segment */}
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={`url(#dashGradient-${i})`}
                strokeWidth="2"
                strokeLinecap="round"
                filter="url(#sparkle)"
                opacity="0.8"
              >
                <animate
                  attributeName="opacity"
                  values="0.5;1;0.5"
                  dur={`${duration}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-width"
                  values="1.5;2.5;1.5"
                  dur={`${duration}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
              </line>
              
              {/* Bright sparkle point at center of dash */}
              <circle
                cx={(x1 + x2) / 2}
                cy={(y1 + y2) / 2}
                r="1"
                fill="#FFD700"
                filter="url(#sparkle)"
                opacity="1"
              >
                <animate
                  attributeName="r"
                  values="0.5;2;0.5"
                  dur={`${duration * 0.8}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.6;1;0.6"
                  dur={`${duration * 0.8}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          );
        })}
      </svg>
      
      {/* Content - with padding to prevent border overlap */}
      <div className="relative z-10" style={{ position: 'relative', padding: '6px' }}>
        {children}
      </div>
    </div>
  );
}
